// src/components/admin/IconPicker.tsx - Comprehensive Icon Picker Component
import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  IconButton,
  Tooltip,
  InputAdornment,
  Chip,
  Paper,
  alpha,
  useTheme
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  // Business & Industry Icons
  Business,
  Factory,
  Store,
  LocalShipping,
  Engineering,
  Agriculture,
  Apartment,
  AccountBalance,
  AttachMoney,
  TrendingUp,
  Analytics,
  Assessment,
  BarChart,
  PieChart,
  ShowChart,
  Timeline,
  // Technology Icons
  Computer,
  PhoneAndroid,
  Devices,
  Memory,
  Storage,
  Cloud,
  CloudQueue,
  DeveloperMode,
  Code,
  Terminal,
  Biotech,
  Science,
  Psychology,
  Lightbulb,
  // Energy & Environment
  Bolt,
  ElectricBolt,
  WaterDrop,
  LocalFireDepartment,
  EnergySavingsLeaf,
  WbSunny,
  Recycling,
  Park,
  Forest,
  Nature,
  // Food & Agriculture
  Restaurant,
  Fastfood,
  LocalDining,
  LocalBar,
  LocalCafe,
  Icecream,
  EmojiFoodBeverage,
  SetMeal,
  RiceBowl,
  LocalPizza,
  // Health & Medical
  LocalHospital,
  Medication,
  Vaccines,
  HealthAndSafety,
  MedicalServices,
  Healing,
  FitnessCenter,
  SportsGymnastics,
  SelfImprovement,
  Spa,
  // Transportation & Logistics
  DirectionsCar,
  DirectionsBus,
  Train,
  DirectionsBoat,
  Flight,
  LocalAirport,
  RocketLaunch,
  TwoWheeler,
  ElectricCar,
  ElectricScooter,
  // Construction & Real Estate
  Construction,
  HomeWork,
  House,
  Cottage,
  Villa,
  OtherHouses,
  Foundation,
  Roofing,
  Plumbing,
  Handyman,
  // Education & Training
  School,
  MenuBook,
  AutoStories,
  LibraryBooks,
  LocalLibrary,
  Bookmark,
  Class,
  CastForEducation,
  Calculate,
  Functions,
  // Communication & Media
  Phone,
  Email,
  Wifi,
  SignalCellularAlt,
  Satellite,
  Radio,
  Tv,
  Podcasts,
  Videocam,
  CameraAlt,
  PhotoCamera,
  // Finance & Banking
  AccountBalanceWallet,
  CreditCard,
  Payments,
  CurrencyExchange,
  MoneyOff,
  PriceCheck,
  Receipt,
  PointOfSale,
  ShoppingCart,
  ShoppingBag,
  // Travel & Tourism
  Hotel,
  Luggage,
  BeachAccess,
  Landscape,
  Hiking,
  Tour,
  Castle,
  Museum,
  TheaterComedy,
  Festival,
  // Entertainment & Sports
  Sports,
  SportsBaseball,
  SportsSoccer,
  SportsFootball,
  SportsTennis,
  SportsEsports,
  VideogameAsset,
  Movie,
  Theaters,
  LiveTv,
  // Retail & Commerce
  Storefront,
  LocalMall,
  LocalGroceryStore,
  Inventory,
  ShoppingBasket,
  AddShoppingCart,
  LocalOffer,
  Discount,
  Sell,
  // Service Icons
  CleaningServices,
  DryCleaning,
  LocalLaundryService,
  Checkroom,
  ContentCut,
  DesignServices,
  ColorLens,
  Brush,
  // Tools & Equipment
  Build,
  Settings,
  Hardware,
  Category,
  Extension,
  Dashboard,
  // People & Community
  People,
  Groups,
  Person,
  EmojiPeople,
  Diversity1,
  Wc,
  FamilyRestroom,
  ChildCare,
  Accessibility,
  // Documents & Data
  Description,
  Article,
  Assignment,
  Topic,
  Folder,
  InsertDriveFile,
  PictureAsPdf,
  Source,
  DataObject,
  // Security & Safety
  Security,
  Shield,
  VerifiedUser,
  Lock,
  Key,
  AdminPanelSettings,
  Policy,
  GppGood,
  LocalPolice,
  // Other Icons
  Star,
  Grade,
  Diamond,
  Verified,
  Celebration,
  EmojiEvents,
  Circle,
} from '@mui/icons-material';

// Comprehensive icon library with categories
const iconLibrary = {
  'Business & Industry': [
    { name: 'Business', icon: Business },
    { name: 'Factory', icon: Factory },
    { name: 'Store', icon: Store },
    { name: 'LocalShipping', icon: LocalShipping },
    { name: 'Engineering', icon: Engineering },
    { name: 'Agriculture', icon: Agriculture },
    { name: 'Apartment', icon: Apartment },
    { name: 'AccountBalance', icon: AccountBalance },
    { name: 'AttachMoney', icon: AttachMoney },
    { name: 'TrendingUp', icon: TrendingUp },
    { name: 'Analytics', icon: Analytics },
    { name: 'Assessment', icon: Assessment },
    { name: 'BarChart', icon: BarChart },
    { name: 'PieChart', icon: PieChart },
    { name: 'ShowChart', icon: ShowChart },
    { name: 'Timeline', icon: Timeline },
  ],
  'Technology & Innovation': [
    { name: 'Computer', icon: Computer },
    { name: 'PhoneAndroid', icon: PhoneAndroid },
    { name: 'Devices', icon: Devices },
    { name: 'Memory', icon: Memory },
    { name: 'Storage', icon: Storage },
    { name: 'Cloud', icon: Cloud },
    { name: 'CloudQueue', icon: CloudQueue },
    { name: 'DeveloperMode', icon: DeveloperMode },
    { name: 'Code', icon: Code },
    { name: 'Terminal', icon: Terminal },
    { name: 'Biotech', icon: Biotech },
    { name: 'Science', icon: Science },
    { name: 'Psychology', icon: Psychology },
    { name: 'Lightbulb', icon: Lightbulb },
  ],
  'Energy & Environment': [
    { name: 'Bolt', icon: Bolt },
    { name: 'ElectricBolt', icon: ElectricBolt },
    { name: 'WaterDrop', icon: WaterDrop },
    { name: 'LocalFireDepartment', icon: LocalFireDepartment },
    { name: 'EnergySavingsLeaf', icon: EnergySavingsLeaf },
    { name: 'WbSunny', icon: WbSunny },
    { name: 'Recycling', icon: Recycling },
    { name: 'Park', icon: Park },
    { name: 'Forest', icon: Forest },
    { name: 'Nature', icon: Nature },
  ],
  'Food & Beverage': [
    { name: 'Restaurant', icon: Restaurant },
    { name: 'Fastfood', icon: Fastfood },
    { name: 'LocalDining', icon: LocalDining },
    { name: 'LocalBar', icon: LocalBar },
    { name: 'LocalCafe', icon: LocalCafe },
    { name: 'Icecream', icon: Icecream },
    { name: 'EmojiFoodBeverage', icon: EmojiFoodBeverage },
    { name: 'SetMeal', icon: SetMeal },
    { name: 'RiceBowl', icon: RiceBowl },
    { name: 'LocalPizza', icon: LocalPizza },
  ],
  'Health & Medical': [
    { name: 'LocalHospital', icon: LocalHospital },
    { name: 'Medication', icon: Medication },
    { name: 'Vaccines', icon: Vaccines },
    { name: 'HealthAndSafety', icon: HealthAndSafety },
    { name: 'MedicalServices', icon: MedicalServices },
    { name: 'Healing', icon: Healing },
    { name: 'FitnessCenter', icon: FitnessCenter },
    { name: 'SportsGymnastics', icon: SportsGymnastics },
    { name: 'SelfImprovement', icon: SelfImprovement },
    { name: 'Spa', icon: Spa },
  ],
  'Transportation & Logistics': [
    { name: 'DirectionsCar', icon: DirectionsCar },
    { name: 'DirectionsBus', icon: DirectionsBus },
    { name: 'Train', icon: Train },
    { name: 'DirectionsBoat', icon: DirectionsBoat },
    { name: 'Flight', icon: Flight },
    { name: 'LocalAirport', icon: LocalAirport },
    { name: 'RocketLaunch', icon: RocketLaunch },
    { name: 'TwoWheeler', icon: TwoWheeler },
    { name: 'ElectricCar', icon: ElectricCar },
    { name: 'ElectricScooter', icon: ElectricScooter },
    { name: 'LocalShipping', icon: LocalShipping },
  ],
  'Construction & Real Estate': [
    { name: 'Construction', icon: Construction },
    { name: 'HomeWork', icon: HomeWork },
    { name: 'House', icon: House },
    { name: 'Cottage', icon: Cottage },
    { name: 'Villa', icon: Villa },
    { name: 'OtherHouses', icon: OtherHouses },
    { name: 'Foundation', icon: Foundation },
    { name: 'Roofing', icon: Roofing },
    { name: 'Plumbing', icon: Plumbing },
    { name: 'Handyman', icon: Handyman },
  ],
  'Education & Training': [
    { name: 'School', icon: School },
    { name: 'MenuBook', icon: MenuBook },
    { name: 'AutoStories', icon: AutoStories },
    { name: 'LibraryBooks', icon: LibraryBooks },
    { name: 'LocalLibrary', icon: LocalLibrary },
    { name: 'Bookmark', icon: Bookmark },
    { name: 'Class', icon: Class },
    { name: 'CastForEducation', icon: CastForEducation },
    { name: 'Calculate', icon: Calculate },
    { name: 'Functions', icon: Functions },
  ],
  'Communication & Media': [
    { name: 'Phone', icon: Phone },
    { name: 'Email', icon: Email },
    { name: 'Wifi', icon: Wifi },
    { name: 'SignalCellularAlt', icon: SignalCellularAlt },
    { name: 'Satellite', icon: Satellite },
    { name: 'Radio', icon: Radio },
    { name: 'Tv', icon: Tv },
    { name: 'Podcasts', icon: Podcasts },
    { name: 'Videocam', icon: Videocam },
    { name: 'CameraAlt', icon: CameraAlt },
    { name: 'PhotoCamera', icon: PhotoCamera },
  ],
  'Finance & Banking': [
    { name: 'AccountBalanceWallet', icon: AccountBalanceWallet },
    { name: 'CreditCard', icon: CreditCard },
    { name: 'Payments', icon: Payments },
    { name: 'CurrencyExchange', icon: CurrencyExchange },
    { name: 'MoneyOff', icon: MoneyOff },
    { name: 'PriceCheck', icon: PriceCheck },
    { name: 'Receipt', icon: Receipt },
    { name: 'PointOfSale', icon: PointOfSale },
    { name: 'ShoppingCart', icon: ShoppingCart },
    { name: 'ShoppingBag', icon: ShoppingBag },
  ],
  'Travel & Tourism': [
    { name: 'Hotel', icon: Hotel },
    { name: 'Luggage', icon: Luggage },
    { name: 'BeachAccess', icon: BeachAccess },
    { name: 'Landscape', icon: Landscape },
    { name: 'Hiking', icon: Hiking },
    { name: 'Tour', icon: Tour },
    { name: 'Castle', icon: Castle },
    { name: 'Museum', icon: Museum },
    { name: 'TheaterComedy', icon: TheaterComedy },
    { name: 'Festival', icon: Festival },
  ],
  'Entertainment & Sports': [
    { name: 'Sports', icon: Sports },
    { name: 'SportsBaseball', icon: SportsBaseball },
    { name: 'SportsSoccer', icon: SportsSoccer },
    { name: 'SportsFootball', icon: SportsFootball },
    { name: 'SportsTennis', icon: SportsTennis },
    { name: 'SportsEsports', icon: SportsEsports },
    { name: 'VideogameAsset', icon: VideogameAsset },
    { name: 'Movie', icon: Movie },
    { name: 'Theaters', icon: Theaters },
    { name: 'LiveTv', icon: LiveTv },
  ],
  'Retail & Commerce': [
    { name: 'Storefront', icon: Storefront },
    { name: 'LocalMall', icon: LocalMall },
    { name: 'LocalGroceryStore', icon: LocalGroceryStore },
    { name: 'Inventory', icon: Inventory },
    { name: 'ShoppingBasket', icon: ShoppingBasket },
    { name: 'AddShoppingCart', icon: AddShoppingCart },
    { name: 'LocalOffer', icon: LocalOffer },
    { name: 'Discount', icon: Discount },
    { name: 'Sell', icon: Sell },
    { name: 'Store', icon: Store },
  ],
  'Services': [
    { name: 'CleaningServices', icon: CleaningServices },
    { name: 'DryCleaning', icon: DryCleaning },
    { name: 'LocalLaundryService', icon: LocalLaundryService },
    { name: 'Checkroom', icon: Checkroom },
    { name: 'ContentCut', icon: ContentCut },
    { name: 'DesignServices', icon: DesignServices },
    { name: 'ColorLens', icon: ColorLens },
    { name: 'Brush', icon: Brush },
  ],
  'Tools & Equipment': [
    { name: 'Build', icon: Build },
    { name: 'Settings', icon: Settings },
    { name: 'Hardware', icon: Hardware },
    { name: 'Category', icon: Category },
    { name: 'Extension', icon: Extension },
    { name: 'Dashboard', icon: Dashboard },
    { name: 'Engineering', icon: Engineering },
  ],
  'People & Community': [
    { name: 'People', icon: People },
    { name: 'Groups', icon: Groups },
    { name: 'Person', icon: Person },
    { name: 'EmojiPeople', icon: EmojiPeople },
    { name: 'Diversity1', icon: Diversity1 },
    { name: 'Wc', icon: Wc },
    { name: 'FamilyRestroom', icon: FamilyRestroom },
    { name: 'ChildCare', icon: ChildCare },
    { name: 'Accessibility', icon: Accessibility },
  ],
  'Documents & Data': [
    { name: 'Description', icon: Description },
    { name: 'Article', icon: Article },
    { name: 'Assignment', icon: Assignment },
    { name: 'Topic', icon: Topic },
    { name: 'Folder', icon: Folder },
    { name: 'InsertDriveFile', icon: InsertDriveFile },
    { name: 'PictureAsPdf', icon: PictureAsPdf },
    { name: 'Source', icon: Source },
    { name: 'DataObject', icon: DataObject },
  ],
  'Security & Safety': [
    { name: 'Security', icon: Security },
    { name: 'Shield', icon: Shield },
    { name: 'VerifiedUser', icon: VerifiedUser },
    { name: 'Lock', icon: Lock },
    { name: 'Key', icon: Key },
    { name: 'AdminPanelSettings', icon: AdminPanelSettings },
    { name: 'Policy', icon: Policy },
    { name: 'GppGood', icon: GppGood },
    { name: 'LocalPolice', icon: LocalPolice },
  ],
  'Other': [
    { name: 'Star', icon: Star },
    { name: 'Grade', icon: Grade },
    { name: 'Diamond', icon: Diamond },
    { name: 'Verified', icon: Verified },
    { name: 'Celebration', icon: Celebration },
    { name: 'EmojiEvents', icon: EmojiEvents },
    { name: 'Circle', icon: Circle },
  ],
};

interface IconPickerProps {
  open: boolean;
  selectedIcon: string;
  onClose: () => void;
  onSelect: (iconName: string) => void;
}

const IconPicker: React.FC<IconPickerProps> = ({ open, selectedIcon, onClose, onSelect }) => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  // Flatten all icons for search
  const allIcons = useMemo(() => {
    const icons: { name: string; icon: any; category: string }[] = [];
    Object.entries(iconLibrary).forEach(([category, categoryIcons]) => {
      categoryIcons.forEach((icon) => {
        icons.push({ ...icon, category });
      });
    });
    return icons;
  }, []);

  // Filter icons based on search and category
  const filteredIcons = useMemo(() => {
    let icons = allIcons;

    // Filter by category
    if (selectedCategory !== 'All') {
      icons = icons.filter((icon) => icon.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      icons = icons.filter((icon) =>
        icon.name.toLowerCase().includes(query) ||
        icon.category.toLowerCase().includes(query)
      );
    }

    return icons;
  }, [allIcons, searchQuery, selectedCategory]);

  const categories = ['All', ...Object.keys(iconLibrary)];

  const handleIconSelect = (iconName: string) => {
    onSelect(iconName);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '80vh'
        }
      }}
    >
      <DialogTitle sx={{
        borderBottom: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="h6" fontWeight={600}>
          Select Icon
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Search Bar */}
        <TextField
          fullWidth
          placeholder="Search icons..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 3 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {/* Category Filter */}
        <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {categories.map((category) => (
            <Chip
              key={category}
              label={category}
              onClick={() => setSelectedCategory(category)}
              color={selectedCategory === category ? 'primary' : 'default'}
              variant={selectedCategory === category ? 'filled' : 'outlined'}
              sx={{ borderRadius: 2 }}
            />
          ))}
        </Box>

        {/* Icon Grid */}
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            maxHeight: '400px',
            overflow: 'auto',
            borderRadius: 2
          }}
        >
          {filteredIcons.length === 0 ? (
            <Typography color="text.secondary" textAlign="center" py={4}>
              No icons found
            </Typography>
          ) : (
            <Grid container spacing={1}>
              {filteredIcons.map((icon) => {
                const IconComponent = icon.icon;
                const isSelected = selectedIcon === icon.name;

                return (
                  <Grid item xs={3} sm={2} md={1.5} key={icon.name}>
                    <Tooltip title={icon.name} arrow>
                      <Paper
                        onClick={() => handleIconSelect(icon.name)}
                        sx={{
                          p: 1.5,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s',
                          border: isSelected
                            ? `2px solid ${theme.palette.primary.main}`
                            : '1px solid transparent',
                          bgcolor: isSelected
                            ? alpha(theme.palette.primary.main, 0.1)
                            : 'transparent',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                            transform: 'scale(1.05)',
                            borderColor: theme.palette.primary.main,
                          },
                        }}
                      >
                        <IconComponent
                          sx={{
                            fontSize: 28,
                            color: isSelected
                              ? theme.palette.primary.main
                              : theme.palette.text.primary
                          }}
                        />
                      </Paper>
                    </Tooltip>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Paper>

        {selectedIcon && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Selected: <strong>{selectedIcon}</strong>
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button onClick={onClose} sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            if (selectedIcon) {
              onClose();
            }
          }}
          variant="contained"
          disabled={!selectedIcon}
          sx={{ borderRadius: 2 }}
        >
          Select
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IconPicker;
